// Live preview for the "watch" card: a small image over signaling, so a viewer sees the stream without decoding it.

export const THUMBNAIL_INTERVAL = 15_000
const WIDTH = 480
/** Data URL length; a signal goes to every peer, so this stays far below the socket's limits. */
const MAX_LENGTH = 32_000

interface FrameGrabber {
    grabFrame(): Promise<ImageBitmap>
}
type ImageCaptureCtor = new (track: MediaStreamTrack) => FrameGrabber

async function grabFromVideo(stream: MediaStream): Promise<HTMLVideoElement | null> {
    const video = document.createElement('video')
    video.muted = true
    video.playsInline = true
    video.srcObject = stream
    try {
        await video.play()
        if (video.videoWidth === 0) {
            await new Promise((resolve) => {
                video.addEventListener('loadeddata', resolve, { once: true })
                setTimeout(resolve, 1000)
            })
        }
        return video.videoWidth > 0 ? video : null
    } catch {
        return null
    }
}

/** A JPEG of the stream's current frame, or `null` when the browser can't give one. */
export async function captureThumbnail(stream: MediaStream): Promise<string | null> {
    const track = stream.getVideoTracks()[0]
    if (!track || track.readyState === 'ended' || typeof document === 'undefined') return null

    let source: CanvasImageSource | null = null
    let width = 0
    let height = 0
    let release = () => undefined as void
    const Capture = (globalThis as unknown as { ImageCapture?: ImageCaptureCtor }).ImageCapture
    try {
        if (Capture) {
            const bitmap = await new Capture(track).grabFrame()
            source = bitmap
            width = bitmap.width
            height = bitmap.height
            release = () => bitmap.close()
        }
    } catch {
        source = null
    }
    if (!source) {
        const video = await grabFromVideo(stream)
        if (!video) return null
        source = video
        width = video.videoWidth
        height = video.videoHeight
        release = () => {
            video.pause()
            video.srcObject = null
        }
    }

    try {
        if (width <= 0 || height <= 0) return null
        const canvas = document.createElement('canvas')
        canvas.width = Math.min(WIDTH, width)
        canvas.height = Math.round((canvas.width * height) / width)
        const context = canvas.getContext('2d')
        if (!context) return null
        context.drawImage(source, 0, 0, canvas.width, canvas.height)
        for (const quality of [0.6, 0.4, 0.25]) {
            const image = canvas.toDataURL('image/jpeg', quality)
            if (image.length <= MAX_LENGTH) return image
        }
        return null
    } finally {
        release()
    }
}

/** Thumbnails come from other people: only a small base64 JPEG ever reaches an <img>. */
export function sanitizeThumbnail(raw: unknown): string | null {
    if (typeof raw !== 'string' || raw.length > MAX_LENGTH) return null
    return /^data:image\/jpeg;base64,[A-Za-z0-9+/]+={0,2}$/.test(raw) ? raw : null
}
